import {HttpResponse} from '@angular/common/http';
import {AbstractControl} from '@angular/forms';
import {DateTime} from 'luxon';
import {MonoTypeOperatorFunction, tap} from 'rxjs';
import {TranslateService} from './translate/translate.service';

export function includesIgnoreCase(
    source: string | null | undefined,
    searchString: string | null | undefined,
): boolean {
    return (source ?? '').toLowerCase().includes((searchString?.trim() ?? '').toLowerCase());
}

export type StreetAddress = {
    address1: string | null | undefined;
    address2: string | null | undefined;
    postalCode: string | null | undefined;
    city: string | null | undefined;
};

/** One-line address: `"<address1> <address2>, <postalCode> <city>"`, empty parts dropped. */
export function formatStreetAddress(address: StreetAddress): string {
    const street = joinPresent(' ', address.address1, address.address2);
    const area = joinPresent(' ', address.postalCode, address.city);
    return joinPresent(', ', street, area);
}

function joinPresent(separator: string, ...parts: Array<string | null | undefined>): string {
    return parts.filter(part => part != null && part !== '').join(separator);
}

export type FormValue<T extends AbstractControl> = ReturnType<T['getRawValue']>;

/**
 * Trims a value a parent form wrote into a text control. A form can bind a numeric control to a
 * text input, and `disable()`/`enable()` replays that raw value through `valueChanges`, so the
 * value is not always the string the control's type claims. Non-strings pass through untouched.
 */
export function trimIfString<T>(value: T): T | string | null {
    return typeof value === 'string' ? value.trim() : (value ?? null);
}

export function filterNullish<TValue>(value: TValue | null | undefined): value is TValue {
    return value != null;
}

export function emptyToNull(value: string | null): string | null {
    const trimmed = value?.trim() ?? '';
    return trimmed === '' ? null : trimmed;
}

/** For weblang keys shared with ReactOnline - `TranslateService.translate` only fills `{0}`..`{n}`. */
export function replaceNamedPlaceholders(text: string, params: Record<string, string>): string {
    return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (placeholder: string, name: string) =>
        name in params ? params[name] : placeholder,
    );
}

export function parseDate(value: string | null): DateTime | null {
    return value != null ? DateTime.fromISO(value) : null;
}

export function parseIntPreserveNull(value: string | null): number | null {
    return value != null ? parseInt(value, 10) : null;
}

export function parseFormattedDate(value: string | null): DateTime {
    return value != null
        ? DateTime.fromFormat(value, 'yyyy-MM-dd')
        : DateTime.invalid('value is null');
}

export function compare(
    v1: string | number | DateTime | boolean | null,
    v2: string | number | DateTime | boolean | null,
): number {
    if (v1 == null && v2 != null) {
        return 1;
    }
    if (v1 != null && v2 == null) {
        return -1;
    }
    if (v1 == null || v2 == null) {
        return 0;
    }

    const dates = v1 instanceof DateTime && v2 instanceof DateTime;
    if (dates && !v1.isValid && v2.isValid) {
        return 1;
    }
    if (dates && v1.isValid && !v2.isValid) {
        return -1;
    }

    if (typeof v1 === 'string' && typeof v2 === 'string') {
        return v1.toUpperCase() < v2.toUpperCase()
            ? -1
            : v1.toUpperCase() > v2.toUpperCase()
              ? 1
              : 0;
    } else {
        return v1 < v2 ? -1 : v1 > v2 ? 1 : 0;
    }
}

export function objectKeysLowerCase(object: object): any {
    return Object.fromEntries(Object.entries(object).map(([k, v]) => [k.toLowerCase(), v]));
}

// Has to match the class names from variables.scss
export const colorPaletteList = [
    'red',
    'bordeaux',
    'blue',
    'lightblue',
    'turquoise',
    'green',
    'naturalgreen',
    'orange',
];

export type KeyOfType<T, V> = keyof {
    [P in keyof T as T[P] extends V ? P : never]: any;
};

export function formatBytes(bytes: number, decimals: number): string {
    if (bytes === 0) {
        return '0 Bytes';
    }
    const k = 1000;
    const dm = decimals <= 0 ? 0 : decimals || 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export function isBrunataTrue(value: string): boolean {
    if (!value) {
        return false;
    }

    return value === 'Y' || value === 'J';
}

export function downloadFile(filename: string, fileBlob: Blob): void {
    const downloadLink = document.createElement('a');
    downloadLink.href = window.URL.createObjectURL(fileBlob);
    downloadLink.setAttribute('download', filename);
    downloadLink.click();
    URL.revokeObjectURL(downloadLink.href);
}

/**
 * Hand a blob HTTP response to the browser as a download. `fileName` is resolved when the response
 * arrives, and `mimeType` re-wraps the body when the file should carry a type the response does not.
 */
export function downloadResponseAsFile(
    fileName: () => string,
    mimeType?: string,
): MonoTypeOperatorFunction<HttpResponse<Blob>> {
    return tap(response => {
        if (response.body != null) {
            downloadFile(
                fileName(),
                mimeType == null ? response.body : new Blob([response.body], {type: mimeType}),
            );
        }
    });
}

// The opened tab still needs the object URL while it loads, so it cannot be revoked immediately.
const REVOKE_OBJECT_URL_DELAY_MS = 60_000;

/**
 * Show a blob in a new tab, falling back to {@link downloadFile} when a popup blocker rejects it -
 * which it does whenever the click that started the flow was already consumed by a dialog or an
 * HTTP round-trip.
 */
export function openBlobInNewTabOrDownload(fileName: string, fileBlob: Blob): void {
    const url = URL.createObjectURL(fileBlob);
    if (window.open(url) == null) {
        URL.revokeObjectURL(url);
        downloadFile(fileName, fileBlob);
        return;
    }
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_OBJECT_URL_DELAY_MS);
}

/**
 * Trigger a browser download of a data URL (e.g. a canvas-rendered PNG export). The filename is
 * stripped of characters that are illegal in file names. Use {@link downloadFile} for Blob payloads.
 */
export function downloadDataUrl(filename: string, dataUrl: string): void {
    const anchor = document.createElement('a');
    anchor.href = dataUrl;
    anchor.download = filename.replace(/[\\/:*?"<>|]/g, '-').trim();
    anchor.click();
    anchor.remove();
}

/**
 * Suffixes an item name with the shared "<name> - Duplicate" (DA: "Kopi") convention used when
 * duplicating saved exports, alarms, and consumption agents in Online. The suffix text comes from
 * the `utilities_Copy_key` translation, which renders as "Duplicate" in English.
 */
export function buildDuplicateName(name: string, translateService: TranslateService): string {
    return `${name} - ${translateService.translate('utilities_Copy_key')}`;
}
